/**
 * Tips feature data layer.
 *
 *   useTips({ dateOfBirth }) → { tips, categories, disclaimer, ageWeeks, forNow, comingUp,
 *                                earlier, search(q), bookmarks, toggleBookmark(id),
 *                                isBookmarked(id), seen, markSeen(id) }
 *
 *   pickTipOfTheDay({ tips, ageWeeks, seen, dateISO, babyId, category? })
 *     — pure and deterministic; shared with the daily-tip notification scheduler
 *       (WS-N) so the dashboard card and the notification agree for a given date.
 *
 * Storage (per-viewer, try/catch everywhere, sane defaults when unavailable):
 *   bt_tip_bookmarks  JSON array of tip ids
 *   bt_tips_seen      { [id]: 'YYYY-MM-DD' } — written when a tip detail is opened
 *
 * Age bands are half-open [ageWeeksMin, ageWeeksMax) in weeks. No date of birth
 * ⇒ ageWeeks is null and every tip counts as "for now".
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { differenceInWeeks } from 'date-fns';
import catalogue from '../../shared/data/tips.json';
import { TIP_CATEGORY_ORDER, getTipCategory } from './tipsCategories';

export const BOOKMARKS_KEY = 'bt_tip_bookmarks';
export const SEEN_KEY = 'bt_tips_seen';
export const SEEN_WINDOW_DAYS = 30;

const ALL_TIPS = Object.freeze(Array.isArray(catalogue.tips) ? catalogue.tips.slice() : []);
export const TIPS_DISCLAIMER = catalogue.disclaimer || '';

/** Catalogue categories merged with their icon/tone meta, in display order. */
export const TIP_CATEGORY_LIST = (() => {
  const fromJson = Array.isArray(catalogue.categories) ? catalogue.categories : [];
  const byKey = new Map(fromJson.map((c) => [c.key, c]));
  const keys = [...TIP_CATEGORY_ORDER, ...fromJson.map((c) => c.key).filter((k) => !TIP_CATEGORY_ORDER.includes(k))];
  return keys
    .filter((k) => byKey.has(k) || ALL_TIPS.some((t) => t.category === k))
    .map((k) => {
      const meta = getTipCategory(k);
      const json = byKey.get(k) || {};
      return { key: k, label: json.label || meta.label, description: json.description || '', icon: meta.icon, tone: meta.tone };
    });
})();

/** All catalogue tips (frozen). */
export function getAllTips() {
  return ALL_TIPS;
}

export function getTipById(id) {
  return ALL_TIPS.find((t) => t.id === id) || null;
}

// --- Pure helpers ---------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD. */
export function toDateISO(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Whole weeks between the date of birth and `at` (default now); null without a DOB. */
export function ageWeeksAt(dateOfBirth, at = new Date()) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  return Math.max(0, differenceInWeeks(at, dob));
}

export function isInBand(tip, ageWeeks) {
  if (ageWeeks == null) return true;
  const min = Number(tip.ageWeeksMin) || 0;
  const max = tip.ageWeeksMax == null ? Infinity : Number(tip.ageWeeksMax);
  return ageWeeks >= min && ageWeeks < max;
}

/** True for tips that apply from birth until well past toddlerhood. */
export function isAllAges(tip) {
  return (Number(tip.ageWeeksMin) || 0) === 0 && Number(tip.ageWeeksMax) >= 156;
}

/** Deterministic 32-bit string hash (djb2). */
export function hashString(str) {
  let h = 5381;
  const s = String(str);
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Case-insensitive substring match over title + body + tags. */
export function matchesQuery(tip, q) {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return true;
  const hay = `${tip.title || ''} ${tip.body || ''} ${(tip.tags || []).join(' ')}`.toLowerCase();
  return hay.includes(needle);
}

/**
 * Split tips into age sections for a baby of `ageWeeks`.
 * forNow: in band (rotated daily, deterministic); comingUp: bands that start
 * later (soonest first); earlier: bands already passed (most recent first).
 */
export function splitByAge(tips, ageWeeks, dateISO = toDateISO()) {
  if (ageWeeks == null) return { forNow: tips.slice(), comingUp: [], earlier: [] };
  const forNow = [];
  const comingUp = [];
  const earlier = [];
  for (const tip of tips) {
    if (isInBand(tip, ageWeeks)) forNow.push(tip);
    else if ((Number(tip.ageWeeksMin) || 0) > ageWeeks) comingUp.push(tip);
    else earlier.push(tip);
  }
  // Daily rotation of the in-band list so the order shifts without being random.
  if (forNow.length > 1) {
    const offset = hashString(dateISO) % forNow.length;
    const sorted = forNow.slice().sort(byId);
    forNow.splice(0, forNow.length, ...sorted.slice(offset), ...sorted.slice(0, offset));
  }
  comingUp.sort((a, b) => Number(a.ageWeeksMin) - Number(b.ageWeeksMin) || byId(a, b));
  earlier.sort((a, b) => Number(b.ageWeeksMax) - Number(a.ageWeeksMax) || byId(a, b));
  return { forNow, comingUp, earlier };
}

/** ISO date `days` before `dateISO` (local calendar). */
function shiftDateISO(dateISO, days) {
  const [y, m, d] = String(dateISO).split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toDateISO(dt);
}

/**
 * Deterministic tip of the day.
 *
 * candidates = in-band tips not seen in the last 30 days (a tip seen *today*
 * still counts so the pick stays stable within the day) → fallback in-band →
 * fallback all (optionally filtered by category). Stable sort by id, then
 * index = hash(dateISO + babyId) % length.
 */
export function pickTipOfTheDay({ tips = ALL_TIPS, ageWeeks = null, seen = {}, dateISO = toDateISO(), babyId = '', category = null } = {}) {
  const pool = category ? tips.filter((t) => t.category === category) : tips.slice();
  if (!pool.length) return null;
  const inBand = pool.filter((t) => isInBand(t, ageWeeks));
  const cutoff = shiftDateISO(dateISO, -SEEN_WINDOW_DAYS);
  const seenMap = seen && typeof seen === 'object' ? seen : {};
  const recentlySeen = (t) => {
    const when = seenMap[t.id];
    return typeof when === 'string' && when >= cutoff && when < dateISO;
  };
  const unseen = inBand.filter((t) => !recentlySeen(t));
  const candidates = (unseen.length ? unseen : inBand.length ? inBand : pool).slice().sort(byId);
  const index = hashString(`${dateISO}${babyId || ''}`) % candidates.length;
  return candidates[index];
}

// --- Per-viewer store (bookmarks + seen), shared across hook instances -----

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable (private mode / quota) — keep the in-memory copy
  }
}

let bookmarksSnapshot = null; // string[]
let seenSnapshot = null; // { [id]: 'YYYY-MM-DD' }
const listeners = new Set();

function loadBookmarks() {
  if (bookmarksSnapshot) return bookmarksSnapshot;
  const raw = readJSON(BOOKMARKS_KEY, []);
  bookmarksSnapshot = Array.isArray(raw) ? raw.filter((id) => typeof id === 'string') : [];
  return bookmarksSnapshot;
}

function loadSeen() {
  if (seenSnapshot) return seenSnapshot;
  const raw = readJSON(SEEN_KEY, {});
  seenSnapshot = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return seenSnapshot;
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // a listener throwing must not break the others
    }
  });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Current bookmarks (ids). Safe outside React. */
export function getBookmarks() {
  return loadBookmarks();
}

/** Current seen map. Safe outside React (used by the notification scheduler). */
export function getSeen() {
  return loadSeen();
}

export function toggleBookmark(id) {
  if (!id) return;
  const current = loadBookmarks();
  bookmarksSnapshot = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
  writeJSON(BOOKMARKS_KEY, bookmarksSnapshot);
  emit();
}

export function markSeen(id, dateISO = toDateISO()) {
  if (!id) return;
  const current = loadSeen();
  if (current[id] === dateISO) return;
  seenSnapshot = { ...current, [id]: dateISO };
  writeJSON(SEEN_KEY, seenSnapshot);
  emit();
}

// --- Hook -------------------------------------------------------------------

export function useTips({ dateOfBirth } = {}) {
  const bookmarks = useSyncExternalStore(subscribe, loadBookmarks, loadBookmarks);
  const seen = useSyncExternalStore(subscribe, loadSeen, loadSeen);

  const dateISO = toDateISO();
  const ageWeeks = useMemo(() => ageWeeksAt(dateOfBirth), [dateOfBirth]);

  const sections = useMemo(() => splitByAge(ALL_TIPS, ageWeeks, dateISO), [ageWeeks, dateISO]);

  const search = useCallback((q) => ALL_TIPS.filter((t) => matchesQuery(t, q)), []);
  const isBookmarked = useCallback((id) => bookmarks.includes(id), [bookmarks]);

  return {
    tips: ALL_TIPS,
    categories: TIP_CATEGORY_LIST,
    disclaimer: TIPS_DISCLAIMER,
    ageWeeks,
    dateISO,
    forNow: sections.forNow,
    comingUp: sections.comingUp,
    earlier: sections.earlier,
    search,
    bookmarks,
    toggleBookmark,
    isBookmarked,
    seen,
    markSeen,
  };
}

export default useTips;
